import React, { useState, useRef, useEffect } from "react";
import {
    FaWhatsapp, FaTimes, FaPaperPlane, FaPhoneAlt,
    FaLightbulb, FaDraftingCompass, FaCalculator,
    FaChevronRight
} from "react-icons/fa";

const CHATBOT_PHONE = "8999427831";
const WHATSAPP_BASE = `https://wa.me/91${CHATBOT_PHONE}`;

const QUICK_ACTIONS = [
    {
        id: "quote",
        icon: FaCalculator,
        title: "Request Instant Project Quote",
        desc: "Get model pricing & lumen sizing",
        query: "I would like an instant quote and technical specifications for LUMIGO Gobo Projectors.",
    },
    {
        id: "sizing",
        icon: FaLightbulb,
        title: "Optical Sizing & Throw Guide",
        desc: "Calculate distance vs projected image size",
        query: "What projector model do I need for my throw distance?",
    },
    {
        id: "custom-gobo",
        icon: FaDraftingCompass,
        title: "Custom Gobo Artwork Review",
        desc: "Send logo/vector artwork for glass production",
        query: "How do I order a custom dichroic glass gobo for my brand logo?",
    },
    {
        id: "expert",
        icon: FaWhatsapp,
        title: "Talk to Projection Expert",
        desc: "Live consultation on WhatsApp",
        query: "Hello LUMIGO, I need expert consultation on commercial gobo projectors.",
    },
];

const BOT_KNOWLEDGE = [
    {
        keywords: ["quote", "price", "cost", "rate", "buy", "purchase", "pricing"],
        reply: "Our commercial gobo projectors range from 35W indoor models up to 300W architectural facade systems. Indicative prices start from ₹18,500 for compact units to ₹48,500 for long-throw IP65 units. You can request a tailored commercial quote with delivery directly on WhatsApp (+91 8999427831).",
        hasWhatsAppAction: true,
    },
    {
        keywords: ["distance", "throw", "size", "height", "meter", "lens", "ratio", "sizing"],
        reply: "Our standard optical lens ratio is approximately 1:0.33. For example:\n• At 3 Metres distance: ~1 Metre diameter image\n• At 6 Metres distance: ~2 Metres diameter image\n• At 15 Metres distance: ~5 Metres diameter image\nHigh-power 150W and 300W models deliver razor-sharp clarity up to 35+ metres in night/ambient conditions.",
        hasWhatsAppAction: true,
    },
    {
        keywords: ["gobo", "logo", "glass", "custom", "artwork", "design", "dichroic"],
        reply: "We fabricate precision laser-etched dichroic glass gobos from your vector artwork (AI, CDR, PDF). Monochrome gobos take 24–48 hours; multi-colour dichroic gobos take 48–72 hours with PAN-India courier dispatch.",
        hasWhatsAppAction: true,
    },
    {
        keywords: ["waterproof", "outdoor", "ip65", "weather", "rain"],
        reply: "All LUMIGO outdoor models carry certified IP65 ingress protection with silicone seals and sealed cable glands, engineered to withstand torrential monsoon rains, dust, and temperature ranges from -20°C to +50°C.",
        hasWhatsAppAction: true,
    },
    {
        keywords: ["installation", "mount", "fit", "bracket", "power"],
        reply: "Every projector includes a 360-degree rotating bracket for wall, ceiling, or truss mounting. They connect to standard 220V AC power with internal OSRAM drivers and manual focal-length adjustment.",
        hasWhatsAppAction: true,
    },
];

const LumigoChatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            sender: "bot",
            text: "Hello 👋 Welcome to LUMIGO Projection Solutions. How can we help illuminate your project or business today?",
            time: "Just now",
        },
    ]);
    const [inputText, setInputText] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    const handleSendMessage = (textToSend) => {
        const text = (textToSend || inputText).trim();
        if (!text) return;

        const userMsg = {
            sender: "user",
            text,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        };

        setMessages(prev => [...prev, userMsg]);
        setInputText("");
        setIsTyping(true);

        setTimeout(() => {
            const lower = text.toLowerCase();
            const matched = BOT_KNOWLEDGE.find(item =>
                item.keywords.some(kw => lower.includes(kw))
            );

            let botReply = "Thank you for reaching out! Our commercial lighting engineers can guide you on the ideal projector wattage, throw calculations, and pricing for your specific site.";
            let showWhatsApp = true;

            if (matched) {
                botReply = matched.reply;
                showWhatsApp = matched.hasWhatsAppAction;
            }

            setMessages(prev => [
                ...prev,
                {
                    sender: "bot",
                    text: botReply,
                    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                    actionText: showWhatsApp ? "Continue on WhatsApp (8999427831)" : null,
                    actionUrl: `${WHATSAPP_BASE}?text=${encodeURIComponent(`Hello LUMIGO,\n\nRegarding my query: "${text}"\nPlease assist.`)}`,
                },
            ]);
            setIsTyping(false);
        }, 600);
    };

    const handleQuickAction = (action) => {
        handleSendMessage(action.query);
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 font-sans">
            {/* 1. FLOATING CHAT TRIGGER BUTTON */}
            {!isOpen && (
                <div className="relative group">
                    <button
                        onClick={() => setIsOpen(true)}
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#168B53] hover:bg-[#137446] text-white flex items-center justify-center text-2xl sm:text-3xl shadow-2xl hover:scale-105 active:scale-95 transition-all duration-300 relative border-2 border-white/40"
                        aria-label="Open Lumigo Project Assistant"
                        title="Chat with LUMIGO Expert (+91 8999427831)"
                    >
                        <FaWhatsapp className="text-3xl" />
                        {/* Notification Pulse Indicator */}
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#E3B629] rounded-full border-2 border-white animate-ping" />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#E3B629] rounded-full border-2 border-white" />
                    </button>

                    {/* Tooltip on Desktop */}
                    <div className="hidden sm:block absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-[#1B2430] text-white text-xs font-bold py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-white/10">
                        Chat with LUMIGO (+91 8999427831)
                    </div>
                </div>
            )}

            {/* 2. CHATBOT EXPANDED DRAWER MODAL */}
            {isOpen && (
                <div className="bg-[#141B22] border border-white/15 rounded-3xl shadow-2xl w-[92vw] sm:w-[380px] h-[550px] max-h-[85vh] flex flex-col overflow-hidden text-white animate-slideUp">

                    {/* Drawer Header (TripleAI Inspired) */}
                    <div className="bg-[#1B2430] p-4 border-b border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 rounded-full bg-[#168B53] text-white flex items-center justify-center text-lg border border-white/20">
                                    <FaWhatsapp />
                                </div>
                                <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full absolute bottom-0 right-0 border-2 border-[#1B2430]" />
                            </div>
                            <div>
                                <div className="flex items-center gap-1.5">
                                    <h3 className="font-extrabold text-sm text-white">LUMIGO AI Assistant</h3>
                                </div>
                                <p className="text-[10px] text-gray-400 font-mono">
                                    Direct Desk: +91 {CHATBOT_PHONE}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsOpen(false)}
                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white flex items-center justify-center transition"
                            aria-label="Close Chat"
                        >
                            <FaTimes className="text-sm" />
                        </button>
                    </div>

                    {/* Chat Messages Body */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3.5 text-xs">

                        {/* Welcome Card & TripleAI Quick Options */}
                        <div className="bg-[#1E293B]/70 border border-white/10 rounded-2xl p-3.5 space-y-2.5">
                            <div>
                                <span className="text-[10px] text-[#E3B629] font-black uppercase tracking-widest block">
                                    Commercial Lighting Desk
                                </span>
                                <h4 className="font-extrabold text-sm text-white mt-0.5">
                                    Need hardware specs or throw advice?
                                </h4>
                                <p className="text-[11px] text-gray-300 mt-1 leading-relaxed">
                                    Select an instant option or send your requirement directly:
                                </p>
                            </div>

                            <div className="space-y-1.5 pt-1">
                                {QUICK_ACTIONS.map(action => (
                                    <button
                                        key={action.id}
                                        onClick={() => handleQuickAction(action)}
                                        className="w-full bg-[#0F1722]/80 hover:bg-[#E3B629]/15 border border-white/10 hover:border-[#E3B629]/50 rounded-xl p-2.5 text-left flex items-center justify-between group transition-all"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <action.icon className="text-[#E3B629] text-xs flex-shrink-0" />
                                            <div>
                                                <div className="text-xs font-bold text-gray-200 group-hover:text-[#E3B629] transition-colors leading-tight">
                                                    {action.title}
                                                </div>
                                                <div className="text-[10px] text-gray-400">
                                                    {action.desc}
                                                </div>
                                            </div>
                                        </div>
                                        <FaChevronRight className="text-gray-500 text-[10px] group-hover:text-[#E3B629] transition-colors" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Conversational Stream */}
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={`flex flex-col ${msg.sender === "user" ? "items-end" : "items-start"}`}
                            >
                                <div
                                    className={`max-w-[85%] rounded-2xl p-3 leading-relaxed whitespace-pre-line text-xs ${
                                        msg.sender === "user"
                                            ? "bg-[#E3B629] text-[#263746] font-semibold rounded-br-xs"
                                            : "bg-[#1E293B] text-gray-200 border border-white/10 rounded-bl-xs"
                                    }`}
                                >
                                    {msg.text}

                                    {msg.actionUrl && (
                                        <div className="mt-3 pt-2.5 border-t border-white/10">
                                            <a
                                                href={msg.actionUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 bg-[#168B53] hover:bg-[#127244] text-white font-bold text-[11px] py-1.5 px-3 rounded-lg uppercase tracking-wider transition shadow-sm"
                                            >
                                                <FaWhatsapp className="text-sm" />
                                                <span>{msg.actionText || "Chat on WhatsApp"}</span>
                                            </a>
                                        </div>
                                    )}
                                </div>
                                <span className="text-[9px] text-gray-500 mt-1 px-1">
                                    {msg.time}
                                </span>
                            </div>
                        ))}

                        {isTyping && (
                            <div className="flex items-center gap-1.5 bg-[#1E293B] text-gray-400 text-xs py-2 px-3 rounded-xl border border-white/10 w-24">
                                <span className="w-1.5 h-1.5 bg-[#E3B629] rounded-full animate-bounce" />
                                <span className="w-1.5 h-1.5 bg-[#E3B629] rounded-full animate-bounce [animation-delay:0.2s]" />
                                <span className="w-1.5 h-1.5 bg-[#E3B629] rounded-full animate-bounce [animation-delay:0.4s]" />
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input Form */}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSendMessage();
                        }}
                        className="p-3 bg-[#1B2430] border-t border-white/10 flex items-center gap-2"
                    >
                        <input
                            type="text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder="Type a message or projector query..."
                            className="flex-1 bg-[#141B22] border border-white/15 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-[#E3B629]"
                        />
                        <button
                            type="submit"
                            disabled={!inputText.trim()}
                            className="w-10 h-10 rounded-xl bg-[#E3B629] hover:bg-[#cca021] text-[#263746] flex items-center justify-center transition disabled:opacity-40 flex-shrink-0"
                            aria-label="Send Message"
                        >
                            <FaPaperPlane className="text-xs" />
                        </button>
                    </form>

                    {/* Bottom Direct Call / WhatsApp strip */}
                    <div className="bg-[#0F1722] py-2 px-4 border-t border-white/5 flex items-center justify-between text-[10px] text-gray-400">
                        <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            Live Desk Available
                        </span>
                        <a
                            href={`tel:+91${CHATBOT_PHONE}`}
                            className="flex items-center gap-1 hover:text-white transition font-mono font-bold"
                        >
                            <FaPhoneAlt className="text-[#E3B629] text-[9px]" />
                            +91 {CHATBOT_PHONE}
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LumigoChatbot;
